import React, { useState } from 'react';
import { geminiApi } from '../services/geminiApi';
import { toast } from '../utils/toast';

export function BrandingAgent() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [brandingTask, setBrandingTask] = useState('');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [brandProfile, setBrandProfile] = useState({
    name: '',
    tagline: '',
    primaryColor: '#2563eb',
    description: '',
    targetAudience: '',
    tone: '',
    values: []
  });

  const [isEditingProfile, setIsEditingProfile] = useState(true);

  const taskTypes = [
    {
      id: 'social',
      name: 'Social Media Content',
      description: 'Generate engaging social media posts aligned with brand voice'
    },
    {
      id: 'ad',
      name: 'Ad Copy',
      description: 'Create compelling advertising copy for various platforms'
    },
    {
      id: 'email',
      name: 'Email Campaign',
      description: 'Design email marketing campaigns that convert'
    },
    {
      id: 'slogan',
      name: 'Slogan Generator',
      description: 'Generate catchy, brand-aligned slogans and taglines'
    },
    {
      id: 'voice',
      name: 'Brand Voice Guide',
      description: 'Create a comprehensive guide for your brand voice and tone'
    },
    {
      id: 'values',
      name: 'Brand Values',
      description: 'Articulate and expand on your core brand values'
    }
  ];

  const handleProfileUpdate = (field, value) => {
    setBrandProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleValuesUpdate = (value) => {
    const valueArray = value.split(',').map(v => v.trim()).filter(v => v);
    setBrandProfile(prev => ({
      ...prev,
      values: valueArray
    }));
  };

  const generateContent = async (type) => {
    if (!brandProfile.name || !brandProfile.description) {
      toast.error('Please complete your brand profile first');
      setIsEditingProfile(true);
      return;
    }

    setIsGenerating(true);
    setBrandingTask(type);
    setGeneratedContent(null);

    try {
      const prompt = `As a branding expert, help create ${type} content for the following brand:

Brand Profile:
- Name: ${brandProfile.name}
- Tagline: ${brandProfile.tagline}
- Description: ${brandProfile.description}
- Target Audience: ${brandProfile.targetAudience}
- Brand Tone: ${brandProfile.tone}
- Core Values: ${brandProfile.values.join(', ')}

Please generate appropriate ${type} content that:
1. Aligns with the brand voice and values
2. Resonates with the target audience
3. Incorporates current marketing best practices
4. Is ready to use with minimal editing

Include variations and specific platform recommendations where applicable.`;

      const response = await geminiApi.generateText(prompt);
      
      if (response.error) {
        throw new Error(response.error);
      }

      setGeneratedContent(response.content || response.message);
    } catch (error) {
      console.error('Error generating branding content:', error);
      toast.error('Failed to generate branding content');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-2xl overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-400/20 to-transparent"></div>
        </div>
        
        <div className="relative">
          <div className="space-y-4">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-slate-400 animate-pulse"></div>
              <span className="text-sm text-slate-100">AI Branding Assistant</span>
            </div>
            <h1 className="text-4xl font-bold text-white">Brand Manager</h1>
            <p className="text-lg text-slate-200 max-w-xl">
              Create and maintain consistent brand messaging across all your marketing channels
            </p>
          </div>
        </div>
      </div>

      {/* Brand Profile Card */}
      <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Brand Profile</h2>
            <button
              onClick={() => setIsEditingProfile(!isEditingProfile)}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {isEditingProfile ? 'Save Profile' : 'Edit Profile'}
            </button>
          </div>
          
          {isEditingProfile ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                <input
                  type="text"
                  value={brandProfile.name}
                  onChange={(e) => handleProfileUpdate('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter your brand name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                <input
                  type="text"
                  value={brandProfile.tagline}
                  onChange={(e) => handleProfileUpdate('tagline', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Your brand's tagline"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={brandProfile.description}
                  onChange={(e) => handleProfileUpdate('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="Describe your brand and what it offers"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <input
                  type="text"
                  value={brandProfile.targetAudience}
                  onChange={(e) => handleProfileUpdate('targetAudience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Who is your target audience?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Tone</label>
                <input
                  type="text"
                  value={brandProfile.tone}
                  onChange={(e) => handleProfileUpdate('tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Professional, Friendly, Innovative"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Values</label>
                <input
                  type="text"
                  value={brandProfile.values.join(', ')}
                  onChange={(e) => handleValuesUpdate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter values separated by commas"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500">Brand Name</p>
                <p className="mt-1 text-gray-900">{brandProfile.name || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Tagline</p>
                <p className="mt-1 text-gray-900">{brandProfile.tagline || 'Not set'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="mt-1 text-gray-900">{brandProfile.description || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Target Audience</p>
                <p className="mt-1 text-gray-900">{brandProfile.targetAudience || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Brand Tone</p>
                <p className="mt-1 text-gray-900">{brandProfile.tone || 'Not set'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Brand Values</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {brandProfile.values.length > 0 ? (
                    brandProfile.values.map((value, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                        {value}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-900">No values set</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Generation Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {taskTypes.map((task) => (
          <button
            key={task.id}
            onClick={() => generateContent(task.name)}
            disabled={isGenerating}
            className="p-6 bg-white rounded-xl border border-gray-200 hover:border-primary-500 transition-colors duration-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{task.name}</h3>
            <p className="text-sm text-gray-600">{task.description}</p>
          </button>
        ))}
      </div>

      {/* Generated Content Display */}
      {(isGenerating || generatedContent) && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {isGenerating ? 'Generating Content...' : `Generated ${brandingTask}`}
              </h2>
              {generatedContent && (
                <button
                  onClick={() => navigator.clipboard.writeText(generatedContent)}
                  className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Copy to Clipboard
                </button>
              )}
            </div>
            
            {isGenerating ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                <span className="ml-3 text-gray-600">Crafting your content...</span>
              </div>
            ) : (
              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-gray-600">
                  {generatedContent}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandingAgent; 